import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Pagination } from '@/components/common/Pagination';
import { TableZoomControls } from '@/components/common/TableZoomControls';
import { useTableZoom } from '@/lib/hooks/useTableZoom';
import { useAuthStore } from '@/features/auth/stores/auth.store';
import { useRoles, useRoleFilters } from '../hooks';
import { useRoleColumns, RolesDataTable, CreateRoleDialog } from '../components';

export function RolesPage() {
  const { t } = useTranslation();
  const hasAccess = useAuthStore((state) => state.hasAccess);
  const { filters, updateFilter, updateFilters, handleSort } = useRoleFilters();

  const { data, isLoading, isError, error } = useRoles(filters);
  // Zoom is owned here so its control can sit in the Pagination row (left of
  // the per-page select) while the table applies the same value to its rows.
  const tableZoom = useTableZoom();

  const columns = useRoleColumns({
    orderBy: filters.orderBy || '',
    order: filters.order || '',
    onSort: handleSort,
    filters,
  });

  if (isError) {
    return (
      <div className="p-4">
        <p className="text-red-500">{error?.message || 'Error loading roles'}</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex py-2 px-4 justify-between items-center border-b-1">
        <h1 className="text-xl font-semibold">{t('roles')}</h1>
        <div className="flex items-center gap-3">
          {hasAccess('user', 'get') && (
            <Link to="/app/users">
              <Button variant="outline">{t('users')}</Button>
            </Link>
          )}
          {hasAccess('role', 'create') && <CreateRoleDialog />}
        </div>
      </div>
      <div className="px-4 pt-4">
        <RolesDataTable
          tableZoom={tableZoom}
          data={data?.results || []}
          columns={columns}
          isLoading={isLoading}
          filters={filters}
          updateFilter={updateFilter}
        />

        <Pagination
          leadingControls={<TableZoomControls zoom={tableZoom} />}
          page={filters.page}
          limit={filters.limit}
          total={data?.meta?.total ?? data?.total ?? 0}
          onPageChange={(val) => updateFilter('page', val)}
          onLimitChange={(val) => updateFilters({ limit: val, page: 0 })}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}
